import * as Equatable from "_shared/_utilities/equatable";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import _ from "lodash";
import Session from "users/__session/session";
import { AccountType } from "users/account-types/account-type";
import { User } from "users/user";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedContracts,
  MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam,
} from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  AWAITING_YOUR_APPROVAL,
  AWAITING_YOUR_REVIEW,
  HumanReadableProposalStatus,
  ProposalAction,
  ProposalMove,
  ProposalOutcome,
  ProposalStatus,
} from "work/proposals/__constants/proposal-lifecycle";
import ProposalSchema, {
  BaseProposalParams,
  ProposalJSON,
  ProposalNegotiationOperationJSON,
  ProposalParams,
} from "work/proposals/_schemas/proposal-schema";
import { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import ProposalRedlineFactory from "work/proposals/redlining/__factory/proposal-redline-factory";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import WorkAgreementSchema from "work/work-agreements/_schemas/work-agreement-codec";
import { WorkAgreementParams } from "work/work-agreements/_schemas/work-agreement-schema";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import WorkAgreement from "work/work-agreements/work-agreement";
import { Audience } from "./comments/threads/topics/topic-context";
import ProposalRedline from "./redlining/proposal-redline";

type ProposalActionMap = { [key in ProposalAction]: Guid[] };
type ProposalField = (typeof ProposalFieldName)[keyof typeof ProposalFieldName];

type ProposalSaveSnapshot = {
  proposalId?: string;
  contentFingerprint: string;
  reviewersFingerprint: string;
};

type ProposalLocalState = {
  hasLocallyMaterializedCounterRevision?: boolean;
  hasLocallyUnsavedRedlineChanges?: boolean;
};

export type ProposalSaveState = {
  hasActualSaveUpdates: boolean;
  isDirty: boolean;
  proposalHasUnsavedRedlineChanges: boolean;
  proposalIsModified: boolean;
  proposalReviewersAreModified: boolean;
};

type ProposalSaveStateParams = {
  fallbackProposal?: Proposal | null;
  includeExplicitReviewerUpdates?: boolean;
  proposal?: Proposal | null;
  savedProposal?: Proposal | null;
};

/** What the Status column says of an archived proposal that ended this way (#6576). */
const archivedOutcomeStatus: Record<ProposalOutcome, HumanReadableProposalStatus> = {
  [ProposalOutcome.Hired]: HumanReadableProposalStatus.Hired,
  [ProposalOutcome.Canceled]: HumanReadableProposalStatus.Canceled,
  [ProposalOutcome.AwardedElsewhere]: HumanReadableProposalStatus.AwardedElsewhere,
};

export default class Proposal {
  public static get EXAMPLE(): Proposal {
    return new Proposal({
      currentUser: User.EXAMPLE,
      spec: {
        id: Guid.generate(),
        creator: EntityMember.EXAMPLE,
        workAgreement: WorkAgreement.EXAMPLE,
        clientReviewers: [ProposalReviewer.EXAMPLE],
        vendorReviewers: [ProposalReviewer.EXAMPLE],
        negotiable: new AHBoolean(true),
      },
    });
  }

  public static getDefaultSpec(creator: EntityMember): ProposalParams {
    return {
      workAgreement: {
        discount: new Percent(0.03),
      },
      clientReviewers: [],
      vendorReviewers: [],
      negotiable: new AHBoolean(true),
      creator,
    };
  }

  public static fromJSON(params: {
    json?: unknown;
    currentUser: User;
    options?: {
      withRedline?: boolean;
      /** Stable persisted baseline for editors whose decisions live outside ProposalJSON. */
      persistedRedline?: ProposalRedline;
    };
    existingRedline?: ProposalRedline;
  }): Proposal | null | undefined {
    const { json } = params;
    if (isNullOrUndefined(json)) return json;
    const proposalSchema = new ProposalSchema(new WorkAgreementSchema(), params.currentUser);
    const spec = proposalSchema.deserialize(json);
    const localState: ProposalLocalState = {
      hasLocallyMaterializedCounterRevision: Proposal.getLocalCounterRevisionMarker(json),
      hasLocallyUnsavedRedlineChanges: Proposal.getLocalSaveMarker(json),
    };

    const proposal = new Proposal({
      currentUser: params.currentUser,
      localState,
      options: params.options,
      spec: {
        ...spec,
        existingRedline: params.existingRedline ?? spec.existingRedline,
      },
    });

    return proposal;
  }

  public static saveJSONVersionsAreEqual(left?: ProposalJSON | null, right?: ProposalJSON | null): boolean {
    return Proposal.saveSnapshotsAreEqual(
      Proposal.getSaveSnapshotFromJSON(left),
      Proposal.getSaveSnapshotFromJSON(right),
    );
  }

  public static getSaveState(params: ProposalSaveStateParams): ProposalSaveState {
    const { includeExplicitReviewerUpdates = false, savedProposal } = params;
    const proposal = params.proposal ?? params.fallbackProposal ?? null;
    const savedSnapshot = Proposal.getSavedSnapshotForProposal(proposal, savedProposal);
    const proposalHasUnsavedRedlineChanges =
      Proposal.contentDiffersFromSaveSnapshot(proposal, savedSnapshot) ||
      Proposal.hasLocalRedlineChangeSignal(proposal) ||
      Proposal.hasExplicitLocalUpdates(proposal);
    const proposalReviewersAreModified =
      Proposal.reviewersDifferFromSaveSnapshot(proposal, savedSnapshot) ||
      (!savedSnapshot && includeExplicitReviewerUpdates && Boolean(proposal?.reviewersAreModified));
    const proposalIsModified = proposalHasUnsavedRedlineChanges || proposalReviewersAreModified;

    // A proposal's save state is about the proposal. It used to take `areUnsavedComments` as well, so a
    // comment the actor had written made the proposal itself dirty and saveable; comments are written as
    // they are made and never wait for a proposal save, so they are no part of this.
    return {
      hasActualSaveUpdates: Boolean(proposalHasUnsavedRedlineChanges),
      isDirty: Boolean(proposalIsModified),
      proposalHasUnsavedRedlineChanges,
      proposalIsModified,
      proposalReviewersAreModified,
    };
  }

  public static getLocalVersionForUpdate(params: {
    savedProposal?: Proposal | null;
    updatedProposal: Proposal;
  }): Proposal | null {
    const { savedProposal, updatedProposal } = params;
    const updatedSnapshot = Proposal.getSaveSnapshot(updatedProposal);
    const savedSnapshot = Proposal.getSavedSnapshotForProposal(updatedProposal, savedProposal);

    if (Proposal.saveSnapshotsAreEqual(updatedSnapshot, savedSnapshot)) {
      return Proposal.hasLocalRedlineChangeSignal(updatedProposal) || Proposal.hasExplicitLocalUpdates(updatedProposal)
        ? updatedProposal
        : null;
    }

    return updatedProposal;
  }

  public readonly RFPId?: Guid;
  public readonly id?: Guid;
  /**
   * Who created the proposal. Absent for a reader the server may tell neither who that was nor who
   * leads the creator's side (#6623).
   */
  public readonly creator?: EntityMember;
  public readonly createdDate?: AHDate;
  public readonly lastUpdated?: AHDate;
  public readonly workAgreement: WorkAgreement;
  public readonly negotiable: AHBoolean = new AHBoolean(true);
  public readonly responseDueBy?: AHDate;

  /**
   * The hired work agreement this proposal was opened to revise, or undefined for a first-time offer.
   * See `revisesHiredWorkAgreement`.
   */
  public readonly replacesWorkAgreementId?: Guid;
  /**
   * The negotiation this revision continues -- the proposal that was negotiated to hire the agreement
   * it revises. Undefined for a first-time offer, and for a revision opened against an agreement whose
   * proposal could not be read.
   *
   * `replacesWorkAgreementId` names where the terms came from; this names where the conversation about
   * them is. Comments are Messaging forums filed under a negotiation and the change history is that
   * negotiation's stream, so both are read through this id.
   */
  public readonly revisesProposalId?: Guid;
  /**
   * Who holds an unsubmitted draft, and the only person who may submit it: its creator, or the
   * primary negotiator the creator handed it over to (#6579). Undefined once it is submitted.
   */
  public readonly owner?: EntityMember;
  /** The team leader named for the draft creator's side, whom the creator may hand it over to. */
  public readonly primaryNegotiator?: EntityMember;
  public readonly draftHandedOverAt?: AHDate;
  public readonly supersededById?: Guid;
  public readonly supersedes?: ProposalParams;
  public readonly latestRevisionId?: Guid;
  public readonly status: ProposalStatus = ProposalStatus.AwaitingSubmission;
  /**
   * The client hired another firm's proposal for the same work, so this one closed (#576). Its
   * status is Archived, as a canceled one's is; this says why. It names no other firm.
   */
  public readonly awardedElsewhere: boolean = false;
  /**
   * What the proposal needs from the reader, as the server read it from their side's boxes: the
   * rule the list's move chips filter by, so a row's tint and its chip agree (#6473). Undefined
   * when it needs nothing from them.
   */
  public readonly readerMove?: ProposalMove;
  /**
   * How an archived proposal ended, by the rule the archive's outcome chips filter with, so the
   * Status column and the chip that lists the row agree (#6576). Undefined when it has none.
   */
  public readonly outcome?: ProposalOutcome;
  public readonly spec: ProposalParams;
  public readonly availableActions: ProposalActionMap;
  public readonly partyRevision?: ProposalParams;
  public readonly negotiationOperations: readonly ProposalNegotiationOperationJSON[];
  public readonly currentUser: User;
  public readonly redlining?: ProposalRedline;
  public readonly clientReviewersCacheKey: string;
  public readonly vendorReviewersCacheKey: string;
  public readonly reviewersKey: string;
  public hasLocallyMaterializedCounterRevision?: boolean;
  public hasLocallyUnsavedRedlineChanges?: boolean;
  public unresolvedFields = new Set<ProposalField>();

  public get baseSpec(): BaseProposalParams {
    return {
      creator: this.creator,
      workAgreement: this.workAgreement.params,
      clientReviewers: this._clientReviewers,
      vendorReviewers: this._vendorReviewers,
      negotiable: this.negotiable,
    };
  }

  public get workAgreementId(): Guid | undefined {
    return this.workAgreement.id;
  }

  public get name(): ProjectName | undefined {
    return this.workAgreement.name ?? undefined;
  }

  public get description(): ProjectDescription | undefined {
    return this.workAgreement.description ?? undefined;
  }

  public get clientTeam(): ClientTeam | MaskedTeam | null {
    return this.workAgreement.clientTeam ?? null;
  }
  public get clientLeader(): EntityMember | MaskedTeam | null {
    if (Masked.isMasked(this.workAgreement.clientTeam)) {
      return this.workAgreement.clientTeam;
    }
    return this.workAgreement.clientTeam?.leader ?? null;
  }

  public get vendorTeam(): VendorTeam | MaskedTeam | null {
    return this.workAgreement.vendorTeam ?? null;
  }

  public get teamRestricted(): AHBoolean | MaskedTeam {
    return this.workAgreement.teamRestricted;
  }
  public get vendorLeader(): EntityMember | null | MaskedTeam {
    return this.workAgreement.vendor?.clone() ?? null;
  }

  public get startDate(): AHDate | null | undefined {
    return this.workAgreement.startDate;
  }
  public get endDate(): AHDate | null | undefined {
    return this.workAgreement.endDate;
  }

  public get feeSchedule(): FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule {
    if (Masked.isMasked(this.workAgreement.feeSchedule)) {
      return this.workAgreement.feeSchedule;
    }
    return [...this.workAgreement.feeSchedule];
  }

  public get clientPolicies(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this.workAgreement.clientPolicies)) {
      return this.workAgreement.clientPolicies;
    }
    return this.workAgreement.clientPolicies.map((doc) => doc.clone());
  }
  public get vendorPolicies(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this.workAgreement.vendorPolicies)) {
      return this.workAgreement.vendorPolicies;
    }
    return this.workAgreement.vendorPolicies.map((doc) => doc.clone());
  }
  public get conflicts(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this.workAgreement.conflicts)) {
      return this.workAgreement.conflicts;
    }
    return this.workAgreement.conflicts.map((doc) => doc.clone());
  }
  public get conflictsCheckWaived(): AHBoolean | MaskedContracts {
    return this.workAgreement.conflictsCheckWaived;
  }

  public get discount(): Percent | MaskedDiscount {
    return this.workAgreement?.discount ?? new Percent(0);
  }

  public get additionalVendors(): EntityMember[] {
    return this.workAgreement.additionalVendors.map((vendor) => vendor.clone());
  }

  public get clientTeamTemplateIds(): Guid[] {
    return this.workAgreement.clientTeamTemplateIds;
  }
  public get vendorTeamTemplateIds(): Guid[] {
    return this.workAgreement.vendorTeamTemplateIds;
  }
  public get clientFeeScheduleTemplateIds(): Guid[] {
    return this.workAgreement.clientFeeScheduleTemplateIds;
  }
  public get vendorFeeScheduleTemplateIds(): Guid[] {
    return this.workAgreement.vendorFeeScheduleTemplateIds;
  }
  /**
   * The team templates the current user's party has applied to the Vendor Team, which is what that
   * party's template checkboxes show. Each party keeps a list of its own and the server lets a party
   * write only that one, so the other party's list is never read here. Written through
   * {@link updateCurrentPartyTeamTemplateIds}, which picks the same list.
   */
  public get currentPartyTeamTemplateIds(): Guid[] {
    switch (this.currentUser.currentParty(this)) {
      case AccountType.Client:
        return this.clientTeamTemplateIds;
      case AccountType.Vendor:
        return this.vendorTeamTemplateIds;
      default:
        return [];
    }
  }
  /** The fee schedule counterpart of {@link currentPartyTeamTemplateIds}. */
  public get currentPartyFeeScheduleTemplateIds(): Guid[] {
    switch (this.currentUser.currentParty(this)) {
      case AccountType.Client:
        return this.clientFeeScheduleTemplateIds;
      case AccountType.Vendor:
        return this.vendorFeeScheduleTemplateIds;
      default:
        return [];
    }
  }

  public get archiveDate(): AHDate | undefined {
    return this.workAgreement.archiveDate;
  }

  /**
   * When the reader left this work, when what they are reading is the view they kept of the
   * proposal: as it was at that moment, and nothing after (#5833). Undefined on a live read.
   */
  public get frozenAt(): AHDate | undefined {
    return this.workAgreement.frozenAt;
  }

  public get isHired(): boolean {
    return this.workAgreement.hireDate !== undefined;
  }

  public get hireDate(): AHDate | undefined {
    return this.workAgreement.hireDate;
  }

  public get clientReviewers(): ProposalReviewer[] {
    return [...this._clientReviewers];
  }
  public get vendorReviewers(): ProposalReviewer[] {
    return [...this._vendorReviewers];
  }

  public get associatedUserIds(): Guid[] {
    const userIds: Guid[] = [...this.workAgreement.associatedUserIds];
    if (this.creator) {
      userIds.push(this.creator.userId);
    }
    this._clientReviewers.forEach((reviewer) => {
      userIds.push(reviewer.userId);
    });
    this._vendorReviewers.forEach((reviewer) => {
      userIds.push(reviewer.userId);
    });
    return userIds;
  }

  public get isModifiedByRedline(): boolean {
    if (this._isModifiedByRedline === undefined) {
      const redlinedRevision = this.redlinedRevision;
      this._isModifiedByRedline = Boolean(redlinedRevision && !redlinedRevision.isEquivalentTo(this));
    }

    return this._isModifiedByRedline;
  }

  public get displayStatus(): string {
    if (this.status === ProposalStatus.Archived) {
      if (this.outcome) return archivedOutcomeStatus[this.outcome];
      return this.awardedElsewhere ? HumanReadableProposalStatus.AwardedElsewhere : HumanReadableProposalStatus.Archived;
    }

    let displayStatus: HumanReadableProposalStatus | string =
      HumanReadableProposalStatus[this.status as keyof typeof HumanReadableProposalStatus] ??
      HumanReadableProposalStatus.Unknown;

    const currentUserId = this.currentUser.id;
    const clientLeader = this.clientLeader;
    const vendorTeam = this.vendorTeam;
    const isClient = MaskedTeam.isPresentNotMasked(clientLeader) && clientLeader.userId.isEqualTo(currentUserId);
    const isTeamLeader =
      MaskedTeam.isPresentNotMasked(vendorTeam) && vendorTeam.leader?.userId.isEqualTo(currentUserId);
    const isVendor = this.workAgreement.additionalVendors.some((vendor) => vendor.userId.isEqualTo(currentUserId));

    if (
      (this.status === ProposalStatus.AwaitingApprovalByClient && isClient) ||
      (this.status === ProposalStatus.AwaitingApprovalByTeamLeader && isTeamLeader) ||
      (this.status === ProposalStatus.AwaitingApprovalByVendors && isVendor)
    ) {
      displayStatus = AWAITING_YOUR_APPROVAL;
    }

    if (
      this._clientReviewers.some((reviewer) => currentUserId?.isEqualTo(reviewer.userId) && !reviewer.dateApproved) ||
      this._vendorReviewers.some((reviewer) => currentUserId?.isEqualTo(reviewer.userId) && !reviewer.dateApproved)
    ) {
      displayStatus = AWAITING_YOUR_REVIEW;
    }

    return displayStatus;
  }

  public get isCurrentRevision(): boolean {
    return !this.supersededById && (!this.latestRevisionId || this.latestRevisionId.isEqualTo(this.id));
  }

  public get isSubmitted(): boolean {
    return this.status !== ProposalStatus.AwaitingSubmission; // TODO: update this to account for canceled proposals
  }

  /**
   * True when this proposal exists to revise an already-hired work agreement — a change order, opened
   * from terms both parties are already working under rather than as a new offer.
   *
   * The signal is the creation-time replacement link, not `supersedes`: that is the retired
   * per-submission revision chain, and the revise flow has never populated it, so every question the
   * editor asked through it answered "no" for exactly the proposals it was asked about.
   */
  public get revisesHiredWorkAgreement(): boolean {
    return Boolean(this.replacesWorkAgreementId);
  }

  public get isApprovedByBothParties(): boolean {
    return Proposal.approvedByBothPartyStatuses.includes(this.status);
  }

  public get isApprovedByClient(): boolean {
    return Proposal.approvedByClientStatuses.includes(this.status);
  }

  public get isApprovedByTeamLeader(): boolean {
    return Proposal.approvedByTeamLeaderStatuses.includes(this.status);
  }

  public get reviewersAreModified(): boolean {
    return this.reviewersAreModifiedAgainst();
  }

  public get clientReviewersAreModified(): boolean {
    return this.clientReviewersAreModifiedAgainst();
  }

  public get vendorReviewersAreModified(): boolean {
    return this.vendorReviewersAreModifiedAgainst();
  }

  /**
   * Whether the proposal is over: archived, sent back for revision, or with its revision refused.
   * `Work.Proposals.ProposalStatus.ArchivedStatuses` on the server, which the list's Archived chip
   * lists; reading only Archived opened the other two from that chip in the live negotiation view
   * rather than the archived snapshot (#6473 review).
   */
  public get isArchived(): boolean {
    return Proposal.archivedStatuses.includes(this.status);
  }

  public get redline(): ProposalRedline | undefined {
    return this.redlining;
  }

  public get latestSubmittingParty(): AccountType | undefined {
    if (this.status === ProposalStatus.AwaitingSubmission && this.supersedes) {
      return this.associatedPartyForUserId(this.creator?.userId);
    }

    const revisionCreatorParty = this.revisionCreatorParty;
    if (revisionCreatorParty) {
      return revisionCreatorParty;
    }

    const reviseParty = this.reviseActionParty;
    if (reviseParty) {
      return reviseParty === AccountType.Client ? AccountType.Vendor : AccountType.Client;
    }

    if (this.creatorBelongsToClientParty) return AccountType.Client;
    if (this.creatorBelongsToVendorParty) return AccountType.Vendor;

    return undefined;
  }

  public get isModified(): boolean {
    return this.isModifiedAgainst();
  }

  public get redlinedRevision(): Proposal | null {
    if (this._redlinedRevision === undefined) {
      const redlining = this.redlining;
      this._redlinedRevision = redlining
        ? this.createRedlinedRevision(redlining, this.buildRedlinedRevisionSpec(redlining))
        : null;
    }

    return this._redlinedRevision;
  }

  public get hasUnsavedRedlineChanges(): boolean {
    this._hasUnsavedRedlineChanges ??= this.computeHasUnsavedRedlineChanges();

    return this._hasUnsavedRedlineChanges;
  }

  public get persistedRedline(): ProposalRedline | undefined {
    if (!this.persistedRedlineWasComputed) {
      this._persistedRedline = this.computePersistedRedline();
      this.persistedRedlineWasComputed = true;
    }

    return this._persistedRedline;
  }

  constructor(params: {
    currentUser: User;
    localState?: ProposalLocalState;
    spec: ProposalParams;
    options?: {
      withRedline?: boolean;
      /** Stable persisted baseline for editors whose decisions live outside ProposalJSON. */
      persistedRedline?: ProposalRedline;
    };
  }) {
    const { currentUser, spec } = params;
    const withRedline = params.options?.withRedline ?? false;

    this.RFPId = spec.workAgreement.RFPId ?? undefined;
    this.id = spec.id;
    this.creator = spec.creator;
    this.createdDate = spec.createdDate;
    this.lastUpdated = spec.lastUpdated;
    this.negotiable = spec.negotiable ?? new AHBoolean(true);
    this.latestRevisionId = spec.latestRevisionId;
    this.responseDueBy = spec.responseDueBy;

    this.workAgreement = new WorkAgreement(spec.workAgreement);
    this.spec = spec;

    const workAgreementSchema = new WorkAgreementSchema();
    this.proposalSchema = new ProposalSchema(workAgreementSchema, currentUser);

    this.currentUser = currentUser;
    this._clientReviewers = [...spec.clientReviewers];
    this._vendorReviewers = [...spec.vendorReviewers];
    this.clientReviewersCacheKey = Proposal.reviewersCacheKey(this._clientReviewers);
    this.vendorReviewersCacheKey = Proposal.reviewersCacheKey(this._vendorReviewers);
    this.reviewersKey = Proposal.reviewersKey([
      ...this._clientReviewers.map((reviewer) => ({ reviewer, prefix: "client" })),
      ...this._vendorReviewers.map((reviewer) => ({ reviewer, prefix: "vendor" })),
    ]);
    this.supersededById = spec?.supersededById;
    this.status = spec?.status ?? ProposalStatus.AwaitingSubmission;
    this.awardedElsewhere = spec?.awardedElsewhere ?? false;
    this.readerMove = spec?.readerMove;
    this.outcome = spec?.outcome;
    this.availableActions = Proposal.cloneAvailableActions(spec.availableActions);

    this.replacesWorkAgreementId = spec?.replacesWorkAgreementId;
    this.revisesProposalId = spec?.revisesProposalId;
    this.owner = spec?.owner;
    this.primaryNegotiator = spec?.primaryNegotiator;
    this.draftHandedOverAt = spec?.draftHandedOverAt;
    this.supersedes = spec?.supersedes;
    this.partyRevision = spec?.partyRevision;
    this.negotiationOperations = spec.negotiationOperations ?? [];

    this.hasLocallyMaterializedCounterRevision = params.localState?.hasLocallyMaterializedCounterRevision;
    this.hasLocallyUnsavedRedlineChanges = params.localState?.hasLocallyUnsavedRedlineChanges;
    this.persistedRedlineOverride = params.options?.persistedRedline;

    // A proposal has a redline once there is something behind it to redline against: an earlier
    // submission of its own, or -- for a revision of a hired work agreement -- the agreed terms it was
    // opened from. The revision case was missing (#4848): `supersedes` is the retired revision chain
    // and the revise path never sets it, so a freshly opened revision had no redline at all, which is
    // what left the editor rendering its loading indicator with nothing left to load.
    const derivedRedline =
      withRedline && (this.isSubmitted || this.supersedes || this.replacesWorkAgreementId)
        ? ProposalRedlineFactory.fromProposal(this)
        : undefined;
    if (spec.existingRedline) {
      this.redlining = this.shouldUseDerivedRedline(spec.existingRedline, derivedRedline)
        ? derivedRedline
        : spec.existingRedline;
    } else if (derivedRedline) {
      this.redlining = derivedRedline;
    }

    this.unresolvedFields = new Set(spec.unresolvedFields);
  }

  public reviewersAreModifiedAgainst(baselineProposal?: Proposal | null): boolean {
    return (
      this.clientReviewersAreModifiedAgainst(baselineProposal) ||
      this.vendorReviewersAreModifiedAgainst(baselineProposal)
    );
  }

  public clientReviewersAreModifiedAgainst(baselineProposal?: Proposal | null): boolean {
    return Proposal.reviewersAreModified(
      this.clientReviewers,
      baselineProposal?.clientReviewers ?? this.clientReviewers,
    );
  }

  public vendorReviewersAreModifiedAgainst(baselineProposal?: Proposal | null): boolean {
    return Proposal.reviewersAreModified(
      this.vendorReviewers,
      baselineProposal?.vendorReviewers ?? this.vendorReviewers,
    );
  }

  public isModifiedAgainst(baselineProposal?: Proposal | null): boolean {
    if (this.hasUnsavedRedlineChanges) {
      return true;
    }

    const comparisonProposal = baselineProposal ?? this;
    let reviewersModified = false;
    if (
      Masked.isNotMasked(this.clientLeader) &&
      this.clientLeader &&
      this.currentUser.id?.isEqualTo(this.clientLeader.userId)
    ) {
      reviewersModified = this.clientReviewersAreModifiedAgainst(comparisonProposal);
    } else if (
      this.vendorTeam &&
      Masked.isNotMasked(this.vendorTeam) &&
      this.currentUser.id?.isEqualTo(this.vendorTeam.leader?.userId)
    ) {
      reviewersModified = this.vendorReviewersAreModifiedAgainst(comparisonProposal);
    }
    return !this.isEquivalentTo(comparisonProposal) || reviewersModified;
  }

  public clone(overrides: Partial<ProposalParams & WorkAgreementParams> = {}): Proposal {
    const workAgreementOverrides = _.pick(
      overrides,
      Proposal.workAgreementOverrideKeys,
    ) as Partial<WorkAgreementParams>;
    const proposalOverrides = overrides as Partial<ProposalParams>;

    const clonedProposal = new Proposal({
      spec: {
        clientReviewers: this._clientReviewers.map((r) => r.clone()),
        vendorReviewers: this._vendorReviewers.map((r) => r.clone()),
        negotiable: this.negotiable.clone(),
        existingRedline: this.redlining,
        workAgreement: this.workAgreement.clone(workAgreementOverrides).params,
        availableActions: this.availableActions,
        createdDate: this.createdDate?.clone(),
        creator: this.creator?.clone(),
        id: this.id?.clone(),
        lastUpdated: this.lastUpdated?.clone(),
        partyRevision: this.partyRevision,
        replacesWorkAgreementId: this.replacesWorkAgreementId?.clone(),
        revisesProposalId: this.revisesProposalId?.clone(),
        owner: this.owner?.clone(),
        primaryNegotiator: this.primaryNegotiator?.clone(),
        draftHandedOverAt: this.draftHandedOverAt?.clone(),
        supersededById: this.supersededById?.clone(),
        latestRevisionId: this.latestRevisionId?.clone() ?? this.id?.clone(),
        responseDueBy: this.responseDueBy?.clone(),
        status: this.status,
        awardedElsewhere: this.awardedElsewhere,
        readerMove: this.readerMove,
        outcome: this.outcome,
        supersedes: this.supersedes,
        ...proposalOverrides,
      },
      currentUser: this.currentUser,
      options: {
        withRedline: false,
        persistedRedline: this.persistedRedlineOverride,
      },
    });

    clonedProposal.unresolvedFields = new Set(this.unresolvedFields);
    const sourceWithLocalState = this as Proposal & {
      hasLocallyMaterializedCounterRevision?: boolean;
      hasLocallyUnsavedRedlineChanges?: boolean;
    };
    const cloneWithLocalState = clonedProposal as Proposal & {
      hasLocallyMaterializedCounterRevision?: boolean;
      hasLocallyUnsavedRedlineChanges?: boolean;
    };
    cloneWithLocalState.hasLocallyUnsavedRedlineChanges = sourceWithLocalState.hasLocallyUnsavedRedlineChanges;
    cloneWithLocalState.hasLocallyMaterializedCounterRevision =
      sourceWithLocalState.hasLocallyMaterializedCounterRevision;

    return clonedProposal;
  }

  public hasFeeCategory(category: FeeScheduleCategory): boolean {
    if (Masked.isMasked(this.workAgreement.feeSchedule)) return false;
    return this.workAgreement.feeSchedule.some((cat) => cat.name?.isEqualTo(category.name));
  }

  public isAssociatedWithUserId(id: Guid): boolean {
    if (this.creator?.userId.isEqualTo(id)) return true;
    if (this._clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(id))) return true;
    if (this._vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(id))) return true;
    if (this.status !== ProposalStatus.AwaitingSubmission) {
      if (
        Masked.isPresentNotMasked(this.workAgreement.clientTeam) &&
        this.workAgreement.clientTeam.leader?.userId.isEqualTo(id)
      )
        return true;
      if (Masked.isMasked(this.workAgreement.vendorTeam)) return false;
      if (this.workAgreement.vendorTeam?.leader?.userId.isEqualTo(id)) return true;
      if (this.workAgreement.vendorTeam?.members.some((member) => member.userId.isEqualTo(id))) return true;
    }
    return false;
  }

  /**
   * Whether the server offers `userId` Delete on this proposal. For a draft that is its creator
   * alone (`AwaitingSubmissionStatus`), and `availableActions` already names only the reader, so this
   * reads the server's answer rather than comparing creator ids (#6577).
   */
  public isDeletableBy(userId: Guid | undefined): boolean {
    if (!userId) return false;
    return (this.availableActions[ProposalAction.Delete] ?? []).some((member) => member.isEqualTo(userId));
  }

  public getReviewer(userId?: Guid): ProposalReviewer | undefined {
    if (!userId) return undefined;
    return (
      this._clientReviewers.find((reviewer) => reviewer.userId.isEqualTo(userId)) ||
      this._vendorReviewers.find((reviewer) => reviewer.userId.isEqualTo(userId))
    );
  }

  public isModifiedByBuilder(builder?: ProposalBuilder | null): boolean {
    if (!builder?.currentSpec) return false;

    const currentSpec = builder.currentSpec;
    const currentWorkAgreement = this.workAgreement.params;

    const fieldsToCheck = [
      [currentSpec.workAgreement.teamRestricted, this.teamRestricted],
      [currentSpec.workAgreement.clientTeam, this.clientTeam],
      [currentSpec.workAgreement.vendorTeam, this.vendorTeam],
      [currentSpec.workAgreement.name, this.name],
      [currentSpec.workAgreement.description, this.description],
      [currentSpec.responseDueBy, this.responseDueBy],
      [currentSpec.workAgreement.startDate, this.startDate],
      [currentSpec.workAgreement.endDate, this.endDate],
      [currentSpec.workAgreement.conflictsCheckWaived, this.conflictsCheckWaived],
      [currentSpec.workAgreement.discount, this.discount],
    ];

    for (const [specField, thisField] of fieldsToCheck) {
      if ((!specField && thisField) || (specField && !Equatable.areEqual(specField, thisField))) {
        return true;
      }
    }

    const builderFeeSchedule = currentSpec.workAgreement.feeSchedule;
    const currentFeeSchedule = currentWorkAgreement.feeSchedule ?? [];
    if (Masked.isPresentNotMasked(builderFeeSchedule) && Masked.isPresentNotMasked(currentFeeSchedule)) {
      if (!Equatable.unorderedArraysAreEqual(builderFeeSchedule, currentFeeSchedule)) {
        return true;
      }
    }

    const arrayChecks: [unknown[], unknown[]][] = [
      [currentSpec.workAgreement.clientTeamTemplateIds ?? [], currentWorkAgreement.clientTeamTemplateIds ?? []],
      [currentSpec.workAgreement.vendorTeamTemplateIds ?? [], currentWorkAgreement.vendorTeamTemplateIds ?? []],
      [
        currentSpec.workAgreement.clientFeeScheduleTemplateIds ?? [],
        currentWorkAgreement.clientFeeScheduleTemplateIds ?? [],
      ],
      [
        currentSpec.workAgreement.vendorFeeScheduleTemplateIds ?? [],
        currentWorkAgreement.vendorFeeScheduleTemplateIds ?? [],
      ],
      [currentSpec.clientReviewers ?? [], this._clientReviewers],
      [currentSpec.vendorReviewers ?? [], this._vendorReviewers],
    ];

    for (const [specArray, thisArray] of arrayChecks) {
      if (!Equatable.unorderedArraysAreEqual(specArray, thisArray)) return true;
    }

    if (
      !Proposal.documentCollectionsAreEqual(
        currentSpec.workAgreement.clientPolicies ?? [],
        currentWorkAgreement.clientPolicies ?? [],
      )
    ) {
      return true;
    }

    if (
      !Proposal.documentCollectionsAreEqual(
        currentSpec.workAgreement.vendorPolicies ?? [],
        currentWorkAgreement.vendorPolicies ?? [],
      )
    ) {
      return true;
    }

    if (
      !Proposal.documentCollectionsAreEqual(
        currentSpec.workAgreement.conflicts ?? [],
        currentWorkAgreement.conflicts ?? [],
      )
    ) {
      return true;
    }

    return false;
  }

  public toJSON(): ProposalJSON {
    if (!this.id) {
      throw new Error("Cannot serialize a proposal without an id.");
    }
    if (!this.workAgreement) {
      throw new Error("Cannot serialize a proposal without a work agreement.");
    }
    return this.proposalSchema.serialize({
      ...this.spec,
      existingRedline: this.redlining,
    });
  }

  public updateRedline(redline: ProposalRedline): Proposal {
    const persistedRedline = this.persistedRedline;
    const hasLocallyUnsavedRedlineChanges = persistedRedline
      ? !redline.isEqualTo(persistedRedline)
      : Boolean(redline.hasChanges || redline.hasRevisions);
    const updatedProposal = new Proposal({
      currentUser: this.currentUser,
      spec: {
        ...this.spec,
        existingRedline: redline,
      },
      options: {
        persistedRedline: this.persistedRedlineOverride,
      },
    });

    updatedProposal.unresolvedFields = new Set(this.unresolvedFields);
    (updatedProposal as Proposal & { hasLocallyUnsavedRedlineChanges?: boolean }).hasLocallyUnsavedRedlineChanges =
      hasLocallyUnsavedRedlineChanges;

    return updatedProposal;
  }

  public updateClientTeamTemplateIds(ids: Guid[]): Proposal {
    return this.clone({ clientTeamTemplateIds: ids });
  }

  public updateVendorTeamTemplateIds(ids: Guid[]): Proposal {
    return this.clone({ vendorTeamTemplateIds: ids });
  }

  public updateClientFeeScheduleTemplateIds(ids: Guid[]): Proposal {
    return this.clone({ clientFeeScheduleTemplateIds: ids });
  }

  public updateVendorFeeScheduleTemplateIds(ids: Guid[]): Proposal {
    return this.clone({ vendorFeeScheduleTemplateIds: ids });
  }

  /**
   * Records which team templates the current user's party has ticked — the list
   * {@link currentPartyTeamTemplateIds} reads. A user with no party on the proposal has no list to
   * write, and gets the proposal back unchanged.
   */
  public updateCurrentPartyTeamTemplateIds(ids: Guid[]): Proposal {
    switch (this.currentUser.currentParty(this)) {
      case AccountType.Client:
        return this.updateClientTeamTemplateIds(ids);
      case AccountType.Vendor:
        return this.updateVendorTeamTemplateIds(ids);
      default:
        return this;
    }
  }

  /** The fee schedule counterpart of {@link updateCurrentPartyTeamTemplateIds}. */
  public updateCurrentPartyFeeScheduleTemplateIds(ids: Guid[]): Proposal {
    switch (this.currentUser.currentParty(this)) {
      case AccountType.Client:
        return this.updateClientFeeScheduleTemplateIds(ids);
      case AccountType.Vendor:
        return this.updateVendorFeeScheduleTemplateIds(ids);
      default:
        return this;
    }
  }

  /**
   * Replaces the vendor-team leader while preserving the current roster — the leader's own setter,
   * mirroring `updateVendorReviewers`/etc. `/vendorTeam/leader` is a scalar path (see
   * proposal-command-builder.ts's `editableDraftSetupFieldPaths`), so flowing a reverted leader through
   * this method (rather than reconstructing the whole `vendorTeam` object ad hoc at a call site) keeps
   * the diff `queueProposalUpdate` builds against the baseline a plain `/vendorTeam/leader` `FieldSet`,
   * never a raw whole-team overwrite that would also touch `members`.
   */
  public updateVendorTeamLeader(leader: EntityMember | null): Proposal {
    const currentTeam = this.vendorTeam;
    if (Masked.isMasked(currentTeam)) {
      throw new Error("Cannot update vendor team leader when the team is masked");
    }
    return this.clone({
      vendorTeam: new VendorTeam({
        leader: leader ?? undefined,
        members: currentTeam?.members ?? [],
        marketplaceId: currentTeam?.marketplaceId,
      }),
    });
  }

  public updateClientReviewers(reviewers: ProposalReviewer[]): Proposal {
    return this.clone({
      clientReviewers: _.uniqBy(reviewers, (reviewer) => reviewer.userId.value),
    });
  }

  public updateVendorReviewers(reviewers: ProposalReviewer[]): Proposal {
    return this.clone({
      vendorReviewers: _.uniqBy(reviewers, (reviewer) => reviewer.userId.value),
    });
  }

  public getCommentAudienceAndSubscribers(
    isExternal: boolean,
    session: Session,
  ): {
    audience: Audience;
    subscribers: EntityMember[];
  } {
    if (!session.user) throw new Error("Session user is required to get comment audience and subscribers.");

    let audience: Audience;
    const subscribers: EntityMember[] = [];

    subscribers.push(session.user.toEntityMember());

    if (isExternal) {
      audience = Audience.AllReviewers;
      if (this.creator) subscribers.push(this.creator);

      if (Masked.isPresentNotMasked(this.clientLeader)) subscribers.push(this.clientLeader);

      if (Masked.isPresentNotMasked(this.vendorLeader)) subscribers.push(this.vendorLeader);

      subscribers.push(
        ...this._clientReviewers.map((reviewer) => reviewer.entityMember),
        ...this._vendorReviewers.map((reviewer) => reviewer.entityMember),
      );
    } else if (session.context?.viewingAsVendor) {
      audience = Audience.VendorReviewers;
      if (Masked.isPresentNotMasked(this.vendorLeader)) subscribers.push(this.vendorLeader);
      if (Masked.isPresentNotMasked(this.vendorTeam)) {
        if (this.vendorTeam.leader) subscribers.push(this.vendorTeam.leader);
      }

      subscribers.push(...this._vendorReviewers.map((reviewer) => reviewer.entityMember));
    } else {
      audience = Audience.ClientReviewers;
      if (Masked.isPresentNotMasked(this.clientLeader)) subscribers.push(this.clientLeader);
      subscribers.push(...this._clientReviewers.map((reviewer) => reviewer.entityMember));
    }

    return {
      audience,
      subscribers: _.uniqBy(subscribers, (subscriber) => subscriber.userId.value),
    };
  }

  public isEquivalentTo(other?: Proposal): boolean {
    if (!other) return false;
    if ((this.vendorTeam && !this.vendorTeam?.isEqualTo(other.vendorTeam)) || (!this.vendorTeam && other.vendorTeam))
      return false;
    if (!this.hasEquivalentContracts(other)) return false;
    if (!this.discount.isEqualTo(other.discount)) return false;
    if (!this.teamRestricted.isEqualTo(other.teamRestricted)) return false;

    if (
      Masked.isNotMasked(this.workAgreement.feeSchedule) &&
      Masked.isNotMasked(other.feeSchedule) &&
      (this.workAgreement.feeSchedule?.length !== other.feeSchedule.length ||
        !this.workAgreement.feeSchedule?.every(
          (category, index) => Masked.isNotMasked(other.feeSchedule) && category.isEqualTo(other.feeSchedule[index]),
        ))
    ) {
      return false;
    }

    const namesEqual = (!this.name && !other.name) || Equatable.areEqual(this.name, other.name);

    const descriptionsEqual =
      (!this.description && !other.description) || Equatable.areEqual(this.description, other.description);

    const clientTeamsEmpty =
      !Masked.isPresentNotMasked(this.workAgreement.clientTeam) &&
      !Masked.isPresentNotMasked(other.workAgreement.clientTeam);

    const clientTeamsEqual = this.workAgreement.clientTeam?.isEqualTo(other.workAgreement.clientTeam);

    const feeScheduleTemplatesEqual =
      Equatable.unorderedArraysAreEqual(this.clientFeeScheduleTemplateIds, other.clientFeeScheduleTemplateIds) &&
      Equatable.unorderedArraysAreEqual(this.vendorFeeScheduleTemplateIds, other.vendorFeeScheduleTemplateIds);

    const teamTemplatesEqual =
      Equatable.unorderedArraysAreEqual(this.clientTeamTemplateIds, other.clientTeamTemplateIds) &&
      Equatable.unorderedArraysAreEqual(this.vendorTeamTemplateIds, other.vendorTeamTemplateIds);

    return Boolean(
      (clientTeamsEmpty || clientTeamsEqual) &&
      namesEqual &&
      descriptionsEqual &&
      this.negotiable.isEqualTo(other.negotiable) &&
      ((!this.responseDueBy && !other.responseDueBy) || Equatable.areEqual(this.responseDueBy, other.responseDueBy)) &&
      ((!this.startDate && !other.startDate) || Equatable.areEqual(this.startDate, other.startDate)) &&
      ((!this.endDate && !other.endDate) || Equatable.areEqual(this.endDate, other.endDate)) &&
      feeScheduleTemplatesEqual &&
      teamTemplatesEqual &&
      _.isEqual(this.unresolvedFields, other.unresolvedFields),
    );
  }

  private static readonly proposalActions = Object.values(ProposalAction) as ProposalAction[];
  private static readonly approvedByBothPartyStatuses = [
    ProposalStatus.AwaitingApprovalByTeam,
    ProposalStatus.AwaitingHire,
    ProposalStatus.AwaitingApprovalByVendors,
  ];
  private static readonly archivedStatuses: readonly ProposalStatus[] = [
    ProposalStatus.Archived,
    ProposalStatus.RevisionRequested,
    ProposalStatus.RevisionRejected,
  ];

  private static readonly approvedByClientStatuses = [
    ProposalStatus.AwaitingApprovalByTeamLeader,
    ProposalStatus.AwaitingApprovalByTeam,
    ProposalStatus.AwaitingHire,
    ProposalStatus.AwaitingApprovalByVendors,
  ];
  private static readonly approvedByTeamLeaderStatuses = [
    ProposalStatus.AwaitingApprovalByClient,
    ProposalStatus.AwaitingApprovalByTeam,
    ProposalStatus.AwaitingHire,
    ProposalStatus.AwaitingApprovalByVendors,
  ];
  private static readonly workAgreementOverrideKeys: Array<keyof WorkAgreementParams> = [
    "id",
    "RFPId",
    "name",
    "description",
    "clientTeam",
    "additionalVendors",
    "vendorTeam",
    "teamRestricted",
    "feeSchedule",
    "startDate",
    "endDate",
    "discount",
    "clientPolicies",
    "vendorPolicies",
    "conflictsCheckWaived",
    "conflicts",
    "clientTeamTemplateIds",
    "vendorTeamTemplateIds",
    "clientFeeScheduleTemplateIds",
    "vendorFeeScheduleTemplateIds",
    "hireDate",
    "archiveDate",
  ];

  private static getSaveSnapshot(proposal?: Proposal | null): ProposalSaveSnapshot | null {
    if (!proposal) return null;

    const proposalJSON = Proposal.getSnapshotJSON(proposal);
    return Proposal.getSaveSnapshotFromJSON(
      proposalJSON,
      Proposal.getIdValue(proposal) ?? Proposal.getIdValue(proposalJSON),
    );
  }

  private static getSaveSnapshotFromJSON(
    proposalJSON?: object | null,
    proposalId?: string,
  ): ProposalSaveSnapshot | null {
    if (!proposalJSON) return null;

    const normalizedProposalJSON = Proposal.normalizeSaveSnapshotJSON(proposalJSON);
    const clientReviewers = normalizedProposalJSON.clientReviewers ?? [];
    const vendorReviewers = normalizedProposalJSON.vendorReviewers ?? [];
    const proposalContentJSON = { ...normalizedProposalJSON };
    delete proposalContentJSON.clientReviewers;
    delete proposalContentJSON.vendorReviewers;
    delete proposalContentJSON.reviewersAreModified;

    return {
      proposalId: proposalId ?? Proposal.getIdValue(proposalJSON),
      contentFingerprint: Proposal.stableStringify(proposalContentJSON),
      reviewersFingerprint: Proposal.stableStringify({
        clientReviewers: Proposal.normalizeReviewerJSON(clientReviewers),
        vendorReviewers: Proposal.normalizeReviewerJSON(vendorReviewers),
      }),
    };
  }

  private static getSavedSnapshotForProposal(
    proposal: Proposal | null | undefined,
    savedProposal: Proposal | null | undefined,
  ): ProposalSaveSnapshot | null {
    const savedSnapshot = Proposal.getSaveSnapshot(savedProposal);
    if (!proposal?.id || savedSnapshot?.proposalId !== Proposal.getIdValue(proposal)) return null;
    return savedSnapshot;
  }

  private static saveSnapshotsAreEqual(left: ProposalSaveSnapshot | null, right: ProposalSaveSnapshot | null): boolean {
    return Boolean(
      left &&
      right &&
      left.proposalId === right.proposalId &&
      left.contentFingerprint === right.contentFingerprint &&
      left.reviewersFingerprint === right.reviewersFingerprint,
    );
  }

  private static contentDiffersFromSaveSnapshot(
    proposal: Proposal | null | undefined,
    snapshot: ProposalSaveSnapshot | null,
  ): boolean {
    const currentSnapshot = Proposal.getSaveSnapshot(proposal);
    if (!currentSnapshot || !snapshot || currentSnapshot.proposalId !== snapshot.proposalId) return false;
    return currentSnapshot.contentFingerprint !== snapshot.contentFingerprint;
  }

  private static reviewersDifferFromSaveSnapshot(
    proposal: Proposal | null | undefined,
    snapshot: ProposalSaveSnapshot | null,
  ): boolean {
    const currentSnapshot = Proposal.getSaveSnapshot(proposal);
    if (!currentSnapshot || !snapshot || currentSnapshot.proposalId !== snapshot.proposalId) return false;
    return currentSnapshot.reviewersFingerprint !== snapshot.reviewersFingerprint;
  }

  private static hasExplicitLocalUpdates(proposal?: Proposal | null): boolean {
    const proposalWithLocalUpdates = proposal as
      | (Proposal & { hasLocallyUnsavedRedlineChanges?: boolean })
      | null
      | undefined;

    return Boolean(proposalWithLocalUpdates?.hasLocallyUnsavedRedlineChanges);
  }

  private static getLocalSaveMarker(json: unknown): boolean | undefined {
    if (!Proposal.isSnapshotRecord(json)) return undefined;
    const hasLocallyUnsavedRedlineChanges = json.hasLocallyUnsavedRedlineChanges;
    return typeof hasLocallyUnsavedRedlineChanges === "boolean" ? hasLocallyUnsavedRedlineChanges : undefined;
  }

  private static getLocalCounterRevisionMarker(json: unknown): boolean | undefined {
    if (!Proposal.isSnapshotRecord(json)) return undefined;
    const hasLocallyMaterializedCounterRevision = json.hasLocallyMaterializedCounterRevision;
    return typeof hasLocallyMaterializedCounterRevision === "boolean"
      ? hasLocallyMaterializedCounterRevision
      : undefined;
  }

  private static hasLocalRedlineChangeSignal(proposal?: Proposal | null): boolean {
    if (!proposal?.redline) return false;
    const persistedRedline = proposal.persistedRedline;
    if (persistedRedline && typeof proposal.redline.isEqualTo === "function") {
      return !proposal.redline.isEqualTo(persistedRedline);
    }

    return Boolean(proposal.redline.hasChanges || proposal.redline.hasRevisions);
  }

  private static getSnapshotJSON(proposal: Proposal): Record<string, unknown> {
    const proposalWithOptionalJSON = proposal as Proposal & { toJSON?: () => ProposalJSON };
    if (typeof proposalWithOptionalJSON.toJSON === "function") {
      return proposalWithOptionalJSON.toJSON() as Record<string, unknown>;
    }

    const proposalLike = proposal as Proposal & Record<string, unknown>;
    return {
      id: Proposal.getIdValue(proposal),
      clientReviewers: proposal.clientReviewers ?? [],
      vendorReviewers: proposal.vendorReviewers ?? [],
      existingRedlineJSON: proposal.redline,
      hasLocallyUnsavedRedlineChanges: proposalLike.hasLocallyUnsavedRedlineChanges,
      hasUnsavedRedlineChanges: proposalLike.hasUnsavedRedlineChanges,
      lastUpdated: proposalLike.lastUpdated,
      partyRevision: proposalLike.partyRevision,
      reviewersAreModified: proposalLike.reviewersAreModified,
      unresolvedFields: proposalLike.unresolvedFields,
    };
  }

  private static getIdValue(value?: { id?: unknown } | null): string | undefined {
    const id = value?.id;

    if (typeof id === "string") return id;
    if (id instanceof Guid) return id.value;
    if (id && typeof id === "object" && "value" in id && typeof id.value === "string") return id.value;
    return undefined;
  }

  private static normalizeSaveSnapshotJSON(proposalJSON: object): Record<string, unknown> {
    const normalizedProposalJSON = Proposal.deepCloneJSONRecord(proposalJSON);

    delete normalizedProposalJSON.additionalVendors;
    delete normalizedProposalJSON.archiveDate;
    delete normalizedProposalJSON.availableActions;
    delete normalizedProposalJSON.clientTeam;
    delete normalizedProposalJSON.createdDate;
    delete normalizedProposalJSON.creator;
    delete normalizedProposalJSON.hireDate;
    delete normalizedProposalJSON.partyRevision;
    delete normalizedProposalJSON.rfpId;
    delete normalizedProposalJSON.status;
    delete normalizedProposalJSON.awardedElsewhere;
    delete normalizedProposalJSON.readerMove;
    delete normalizedProposalJSON.outcome;
    delete normalizedProposalJSON.replacesWorkAgreementId;
    delete normalizedProposalJSON.revisesProposalId;
    delete normalizedProposalJSON.owner;
    delete normalizedProposalJSON.primaryNegotiator;
    delete normalizedProposalJSON.draftHandedOverAt;
    delete normalizedProposalJSON.supersededById;
    delete normalizedProposalJSON.workAgreementId;
    delete normalizedProposalJSON.hasLocallyUnsavedRedlineChanges;
    delete normalizedProposalJSON.hasLocallyMaterializedCounterRevision;
    delete normalizedProposalJSON.hasUnsavedRedlineChanges;
    delete normalizedProposalJSON.lastUpdated;
    delete normalizedProposalJSON.pendingLeaderHandovers;
    delete normalizedProposalJSON.negotiationOperations;
    delete normalizedProposalJSON.originalProposal;
    delete normalizedProposalJSON.supersedes;

    ProposalRedline.normalizeSaveSnapshotJSON(normalizedProposalJSON.existingRedlineJSON);
    ProposalRedline.normalizeSaveSnapshotJSON(normalizedProposalJSON.redline);

    return normalizedProposalJSON;
  }

  private static deepCloneJSONRecord(value: object): Record<string, unknown> {
    return Proposal.deepCloneJSONValue(value) as Record<string, unknown>;
  }

  private static deepCloneJSONValue(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(Proposal.deepCloneJSONValue);
    if (Proposal.isSnapshotRecord(value)) {
      return Object.fromEntries(
        Object.entries(value).map(([key, entryValue]) => [key, Proposal.deepCloneJSONValue(entryValue)]),
      );
    }
    return value;
  }

  private static isSnapshotRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  private static normalizeReviewerJSON(reviewers: unknown): unknown[] {
    if (!Array.isArray(reviewers)) return [];

    return reviewers.map(Proposal.normalizeReviewerForSnapshot).sort((left, right) => {
      const leftKey = Proposal.getReviewerSnapshotSortKey(left);
      const rightKey = Proposal.getReviewerSnapshotSortKey(right);
      return leftKey.localeCompare(rightKey);
    });
  }

  private static normalizeReviewerForSnapshot(reviewer: unknown): unknown {
    const reviewerWithOptionalJSON = reviewer as { toJSON?: () => unknown };
    const reviewerJSON =
      typeof reviewerWithOptionalJSON?.toJSON === "function" ? reviewerWithOptionalJSON.toJSON() : reviewer;

    if (!Proposal.isSnapshotRecord(reviewerJSON)) return reviewerJSON;
    const normalizedReviewerJSON = Proposal.deepCloneJSONRecord(reviewerJSON);
    delete normalizedReviewerJSON.dateApproved;
    return normalizedReviewerJSON;
  }

  private static getReviewerSnapshotSortKey(reviewer: unknown): string {
    const reviewerRecord = reviewer as Record<string, Guid | string | undefined>;
    const userId = reviewerRecord?.userId;
    const legalEntityId = reviewerRecord?.legalEntityId;

    return `${typeof userId === "string" ? userId : (userId?.value ?? "")}-${
      typeof legalEntityId === "string" ? legalEntityId : (legalEntityId?.value ?? "")
    }`;
  }

  private static stableStringify(value: unknown): string {
    if (Array.isArray(value)) {
      return `[${value.map((item) => Proposal.stableStringify(item)).join(",")}]`;
    }
    if (value && typeof value === "object") {
      const record = value as Record<string, unknown>;
      const entries = Object.keys(record)
        .filter((key) => record[key] !== undefined)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${Proposal.stableStringify(record[key])}`);

      return `{${entries.join(",")}}`;
    }

    return JSON.stringify(value) ?? "undefined";
  }

  private static cloneAvailableActions(
    availableActions?: Partial<Record<ProposalAction, Guid[]>> | null,
  ): ProposalActionMap {
    return Proposal.proposalActions.reduce((clonedActions, action) => {
      clonedActions[action] = [...(availableActions?.[action] ?? [])];
      return clonedActions;
    }, {} as ProposalActionMap);
  }

  private static reviewersCacheKey(reviewers: ProposalReviewer[]): string {
    return _.sortBy(
      reviewers.map(
        (reviewer) =>
          `${reviewer.userId.value}-${reviewer.isEditing}-${reviewer.dateApproved?.toISOString() ?? "not_approved"}`,
      ),
    ).join(",");
  }

  private static reviewersKey(reviewers: Array<{ prefix: string; reviewer: ProposalReviewer }>): string {
    return _.sortBy(
      reviewers.map(({ prefix, reviewer }) => `${prefix}-${reviewer.userId.value}-${reviewer.isEditing}`),
    ).join(",");
  }

  private static reviewersAreModified(
    updatedReviewers: ProposalReviewer[],
    originalReviewers: ProposalReviewer[],
  ): boolean {
    return !Equatable.unorderedArraysAreEqual(updatedReviewers, originalReviewers);
  }

  private static documentCollectionsAreEqual(
    left: WorkDocument[] | MaskedContracts,
    right: WorkDocument[] | MaskedContracts,
  ): boolean {
    if (Masked.isMasked(left) || Masked.isMasked(right)) return true;
    if (left.length !== right.length) return false;

    const unmatchedRight = [...right];
    return left.every((document) => {
      const matchedIndex = unmatchedRight.findIndex((otherDocument) => document.is(otherDocument));
      if (matchedIndex === -1) return false;

      unmatchedRight.splice(matchedIndex, 1);
      return true;
    });
  }

  private readonly _clientReviewers: ProposalReviewer[] = [];
  private readonly _vendorReviewers: ProposalReviewer[] = [];
  private readonly proposalSchema: ProposalSchema;
  private _redlinedRevision?: Proposal | null;
  private _persistedRedline?: ProposalRedline;
  private persistedRedlineWasComputed = false;
  private _hasUnsavedRedlineChanges?: boolean;
  private _isModifiedByRedline?: boolean;
  private readonly persistedRedlineOverride?: ProposalRedline;

  private get revisionCreatorParty(): AccountType | undefined {
    const baselineCreatorParty = this.baselineCreatorParty;

    if (this.partyRevision) {
      const partyRevisionCreatorParty = this.associatedPartyForUserId(this.partyRevision.creator?.userId);
      if (partyRevisionCreatorParty && baselineCreatorParty && partyRevisionCreatorParty !== baselineCreatorParty) {
        return partyRevisionCreatorParty;
      }
    }

    const currentCreatorParty = this.associatedPartyForUserId(this.creator?.userId);
    if (currentCreatorParty && baselineCreatorParty && currentCreatorParty !== baselineCreatorParty) {
      return currentCreatorParty;
    }

    return undefined;
  }

  private get baselineCreatorParty(): AccountType | undefined {
    const baselineCreatorId = this.supersedes?.creator?.userId ?? this.creator?.userId;
    return this.associatedPartyForUserId(baselineCreatorId);
  }

  private get reviseActionParty(): AccountType | undefined {
    let clientCanRevise = false;
    let vendorCanRevise = false;

    for (const userId of this.availableActions[ProposalAction.Revise]) {
      if (this.isClientAssociatedUserId(userId)) {
        clientCanRevise = true;
      }
      if (this.isVendorAssociatedUserId(userId)) {
        vendorCanRevise = true;
      }
    }

    if (clientCanRevise === vendorCanRevise) return undefined;
    return clientCanRevise ? AccountType.Client : AccountType.Vendor;
  }

  private get creatorBelongsToClientParty(): boolean {
    return this.isClientAssociatedUserId(this.creator?.userId);
  }

  private get creatorBelongsToVendorParty(): boolean {
    return this.isVendorAssociatedUserId(this.creator?.userId);
  }

  private associatedPartyForUserId(userId?: Guid): AccountType | undefined {
    if (this.isClientAssociatedUserId(userId)) return AccountType.Client;
    if (this.isVendorAssociatedUserId(userId)) return AccountType.Vendor;
    return undefined;
  }

  private computePersistedRedline(): ProposalRedline | undefined {
    if (this.persistedRedlineOverride) return this.persistedRedlineOverride;
    if (!this.isSubmitted) return this.redlining;

    return new Proposal({
      currentUser: this.currentUser,
      spec: {
        ...this.spec,
        existingRedline: undefined,
      },
      options: {
        withRedline: true,
      },
    }).redline;
  }

  private computeHasUnsavedRedlineChanges(): boolean {
    const redlining = this.redlining;
    if (!redlining && this.partyRevision) {
      console.warn("Party revision exists without redlining");
      return false;
    } else if (!redlining) {
      return false;
    }

    if (this.persistedRedlineOverride) {
      return !redlining.isEqualTo(this.persistedRedlineOverride);
    }

    if (!this.partyRevision) {
      return redlining.hasChanges;
    }

    if (redlining.isResolved && this.lastUpdated?.isAfter(this.partyRevision.lastUpdated)) {
      return true;
    }

    const redlinedRevision = this.redlinedRevision;
    if (!redlinedRevision) {
      return false;
    }

    const redlinedUnresolvedFields = new Set(redlinedRevision.unresolvedFields);
    const partyUnresolvedFields = new Set(this.partyRevision.unresolvedFields);
    if (!_.isEqual(redlinedUnresolvedFields, partyUnresolvedFields)) {
      return true;
    }

    const schema = new WorkAgreementSchema();
    const currentRedlineSpec = schema.serialize(redlinedRevision.baseSpec.workAgreement);
    const partyRedlineSpec = schema.serialize(this.partyRevision.workAgreement);
    return !_.isEqualWith(currentRedlineSpec, partyRedlineSpec, (objValue, othValue, key) => {
      if (key === "id") return true; // Don't compare ids
    });
  }

  private isClientAssociatedUserId(userId?: Guid): boolean {
    if (!userId) return false;
    if (Masked.isNotMasked(this.clientLeader) && this.clientLeader?.userId.isEqualTo(userId)) return true;
    return this._clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(userId));
  }

  private isVendorAssociatedUserId(userId?: Guid): boolean {
    if (!userId) return false;
    if (Masked.isMasked(this.vendorTeam)) return false;
    if (this.vendorLeader && Masked.isNotMasked(this.vendorLeader) && this.vendorLeader.userId.isEqualTo(userId)) {
      return true;
    }
    if (this.vendorTeam?.members.some((member) => member.userId.isEqualTo(userId))) {
      return true;
    }
    return this._vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(userId));
  }

  private createRedlinedRevision(redlining: ProposalRedline, revisionSpec: ProposalParams): Proposal {
    return new Proposal({
      currentUser: this.currentUser,
      spec: {
        ...revisionSpec,
        clientReviewers: this._clientReviewers,
        vendorReviewers: this._vendorReviewers,
        workAgreement: {
          ...revisionSpec.workAgreement,
          clientTeamTemplateIds: this.clientTeamTemplateIds,
          vendorTeamTemplateIds: this.vendorTeamTemplateIds,
          clientFeeScheduleTemplateIds: this.clientFeeScheduleTemplateIds,
          vendorFeeScheduleTemplateIds: this.vendorFeeScheduleTemplateIds,
        },
        unresolvedFields: redlining.unresolvedFields ?? undefined,
      },
      options: {
        withRedline: false,
      },
    });
  }

  private buildRedlinedRevisionSpec(redlining: ProposalRedline): ProposalParams {
    const { vendorTeam, teamRestricted } = this.getRedlinedVendorTeamAndRestriction(redlining);

    return {
      ...this.spec,
      workAgreement: {
        ...this.workAgreement.params,
        ...redlining.currentProjectDates,
        ...redlining.currentDocuments,
        name: redlining.name.currentEntry ?? redlining.name.originalEntry ?? this.name,
        description: redlining.description.currentEntry ?? redlining.description.originalEntry ?? this.description,
        feeSchedule: redlining.currentFeeSchedule,
        vendorTeam,
        clientTeam: this.clientTeam ?? undefined,
        teamRestricted,
        discount: redlining.currentDiscount,
      },
      responseDueBy: redlining.responseDueBy.currentEntry ?? undefined,
    };
  }

  private getRedlinedVendorTeamAndRestriction(redlining: ProposalRedline): {
    vendorTeam: VendorTeam | MaskedTeam;
    teamRestricted: AHBoolean | MaskedTeam;
  } {
    const vendorLeader = this.vendorLeader;
    if (!vendorLeader) {
      throw new Error("Cannot create a redlined revision without a vendor team.");
    }

    const currentTeamMembers = redlining.currentTeamMembers;
    if (
      Masked.isMasked(vendorLeader) ||
      Masked.isMasked(currentTeamMembers) ||
      Masked.isMasked(redlining.teamRestricted)
    ) {
      return {
        vendorTeam: this.vendorTeam as MaskedTeam,
        teamRestricted: this.teamRestricted,
      };
    }

    // `vendorLeader` is `this.vendorTeam.leader` (see the `vendor` getter on `WorkAgreement`), so
    // confirming it is not masked above also confirms `this.vendorTeam` is not masked here — safe to
    // read `marketplaceId` off it and carry it into the redlined team rather than dropping it (#4515).
    return {
      vendorTeam: new VendorTeam({
        leader: vendorLeader,
        members: currentTeamMembers,
        marketplaceId: Masked.isMasked(this.vendorTeam) ? undefined : this.vendorTeam?.marketplaceId,
      }),
      teamRestricted: redlining.teamRestricted.currentEntry ?? new AHBoolean(false),
    };
  }

  private shouldUseDerivedRedline(existingRedline: ProposalRedline, derivedRedline?: ProposalRedline): boolean {
    if (!derivedRedline) return false;
    if (existingRedline.isEqualTo(derivedRedline)) return false;
    if (!this.currentUser.isLatestSubmittingParty(this)) return false;
    const existingRedlineCollapsedToResolvedSnapshot = existingRedline.isResolved && !existingRedline.hasChanges;
    const derivedRedlineStillShowsBlueLines = derivedRedline.hasChanges || !derivedRedline.isResolved;
    const sessionHistoryMatchesDerived = _.isEqual(
      existingRedline.sessionHistory.map((change) => change.toJSON()),
      derivedRedline.sessionHistory.map((change) => change.toJSON()),
    );

    return (
      existingRedlineCollapsedToResolvedSnapshot && derivedRedlineStillShowsBlueLines && sessionHistoryMatchesDerived
    );
  }

  private hasEquivalentContracts(other: Proposal): boolean {
    if (!this.conflictsCheckWaived.isEqualTo(other.conflictsCheckWaived)) return false;

    const workAgreement = this.workAgreement.params;
    const otherWorkAgreement = other.workAgreement.params;

    return (
      Proposal.documentCollectionsAreEqual(workAgreement.conflicts ?? [], otherWorkAgreement.conflicts ?? []) &&
      Proposal.documentCollectionsAreEqual(
        workAgreement.clientPolicies ?? [],
        otherWorkAgreement.clientPolicies ?? [],
      ) &&
      Proposal.documentCollectionsAreEqual(workAgreement.vendorPolicies ?? [], otherWorkAgreement.vendorPolicies ?? [])
    );
  }
}
